import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon";
import LoginUtils from "@/utils/login/LoginUtils";
import type {AxiosRequestConfig} from "axios";
import NamaURLS from "@/utils/constants/NamaURLS";
import GUIDevMode from "@/utils/constants/GUIDevMode";
import CommonConstants from "@/utils/constants/CommonConstants";
import URLRequestParameters from "@/utils/constants/URLRequestParameters";
import DomainBaseFieldIDs from "@/utils/constants/DomainBaseFieldIDs";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import EntityTypesUtils from "@/utils/common/EntityTypesUtils.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import CriteriaValidatorApplicability from "@/utils/constants/CriteriaValidatorApplicability.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import MenuTargetType from "@/utils/constants/MenuTargetType";
import CollectionUtils from "@/utils/common/CollectionUtils.ts";
import MetaData from "@/utils/common/MetaData.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import TopPanelSearchUtils from "@/utils/common/TopPanelSearchUtils.ts";
import Operator from "@/utils/constants/Operator.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import FieldStyle = namaTypes.FieldStyle;
import PrintReportRequest = namaTypes.PrintReportRequest;
import TemplateMetadata = namaTypes.TemplateMetadata;
import ReviseSettings = namaTypes.ReviseSettings;
import CustomRepInfo = namaTypes.CustomRepInfo;
import CustomReportsMetadata = namaTypes.CustomReportsMetadata;
import CustomizedFieldsInfo = namaTypes.CustomizedFieldsInfo;
import UseScannerInFieldInfo = namaTypes.UseScannerInFieldInfo;
import ESignatureInfo = namaTypes.ESignatureInfo;
import DTOAutoDashboardsAndReports = namaTypes.DTOAutoDashboardsAndReports;
import EntityReferenceData = namaTypes.EntityReferenceData;
import DTOMenuItem = namaTypes.DTOMenuItem;
import ListScreenLayout = namaTypes.ListScreenLayout;
import UIPreferences = namaTypes.UIPreferences;
import TooltipMetadata = namaTypes.TooltipMetadata;
import FieldMetaData = namaTypes.FieldMetaData;
import LoginAndGUIPartsResponse = namaTypes.LoginAndGUIPartsResponse;


export default class ClientCache {
   private static _loginSettings: LoginAndGUIPartsResponse | null = null;
  private static customGUISettings: namaTypes.CustomGUIPartsReponse | undefined;
  private static displayCriticalCount = 0;
  private static availableModules: namaTypes.ModuleDescription[] | undefined;
  private static rootReportGroups: namaTypes.EntityReferenceData[] | undefined;
  private static sortedAllEntityTypes: any[];
  private static _entityTypesMetadata: Record<string, namaTypes.EntityTypeMetaData>;
  private static simpleEntityTypesMetadata: Record<string, namaTypes.EntityTypeMetaData>;
  private static _simpleAndAllEntityTypes: string[];
  private static allFeatures: any[];
  private static sortedAllDisabledFeatures: any[];
  private static installedModuleIDs: any[];
  private static dimensionConfigurations: any;
  private static autoDashboardsAndReports: namaTypes.DTOAutoDashboardsAndReports;
  private static rootReportGroupsMergedWithAuto: boolean = false;
  private static _uiPreferencesCache: Record<string, UIPreferences | null> = {};
  private static _termsAndConfigScreens: Record<string, namaTypes.EditScreenLayout | null> = {};
  private static isPrintPossibleMap: Record<string, any> = {};
  private static dashboardGroups: Record<string, EntityReferenceData[]> = {};
  private static generatedIDs: string[] = [];
  private static bgImageUrl: string = '';

  static get loginSettings(): LoginAndGUIPartsResponse | null {
    return this._loginSettings;
  }

  static set loginSettings(value: LoginAndGUIPartsResponse | null) {
    this._loginSettings = value;
  }

  static get entityTypesMetadata(): Record<string, namaTypes.EntityTypeMetaData> {
    return this._entityTypesMetadata;
  }

  static set uiPreferencesCache(value: Record<string, any>) {
    this._uiPreferencesCache = value;
  }

  static shouldShowIds() {
    return useNamaFlagsStore().showIdsActive;
  }

  static shouldShowFieldInfo() {
    return useNamaFlagsStore().showFieldInfoActive;
  }

  static getAllFeatures(): any[] {
    return this.allFeatures
  }

  static getCustomGUISettings(): namaTypes.CustomGUIPartsReponse | undefined {
    return this.customGUISettings;
  }

  static async onLogin(settings: namaTypes.LoginAndGUIPartsResponse) {
    this.loginSettings = settings;
    this.customGUISettings = settings.customGUIPartsReponse;
    let customGUISettings = this.customGUISettings;
    FieldTypesUtils.setStaticYearStart(customGUISettings?.tooltipsAndReportsAndRevise?.calendarYearStart)
    FieldTypesUtils.setStaticYearEnd(customGUISettings?.tooltipsAndReportsAndRevise?.calendarYearEnd)
    FieldTypesUtils.fiscalPeriod = customGUISettings?.tooltipsAndReportsAndRevise?.fiscalPeriod;
    FieldTypesUtils.currentUser = LoginUtils.loginInfo?.userInfo;
    FieldTypesUtils.currentUserEmployee = LoginUtils.loginInfo?.userInfo?.employee;
    FieldTypesUtils.currentUserRelatedSubsidiary = LoginUtils.loginInfo?.userInfo?.settings?.relatedEntity;
    if (customGUISettings?.criticalSystemMessages && customGUISettings.criticalSystemMessages.length) {
      this.displayCriticalCount++;
      if (this.displayCriticalCount <= 3) {
        let errorsStore = useErrorsStore();
        errorsStore.pushErrors(customGUISettings.criticalSystemMessages);
      }
    }
    this.availableModules = settings.moduleList;
    this.rootReportGroups = settings.rootReportGroups;
    this.availableModules?.sort((o1, o2) => NamaCommon.namaCompare(o1, o2));
    this.uiPreferencesCache = {};
    this.sortedAllEntityTypes = [];
    this._entityTypesMetadata = {};
    this.simpleEntityTypesMetadata = {};
    this.allFeatures = [];
    this.sortedAllDisabledFeatures = [...settings.loginInfo?.userPreventedEntities ?? [], ...settings.loginInfo?.userPreventedFeatures ?? []];
    this.sortedAllDisabledFeatures.sort();
    let disabledEntities: any[] = [];
    this.installedModuleIDs = [];
    for (let module of this.availableModules ?? []) {
      module.entitiesMetaData?.entityTypesMetaData?.sort((o1, o2) => NamaCommon.namaCompare(o1.entityType, o2.entityType));
      this.installedModuleIDs.push(module.moduleId, ...module.embededModules!)
      this.allFeatures.push(...new Set(module.definedFeatures ?? []));
      for (let entity of module.entitiesMetaData?.entityTypesMetaData ?? []) {
        this._entityTypesMetadata[entity.entityType!] = entity;
        if ((entity.relatedToFeature && this.sortedAllDisabledFeatures.binaryContains(entity.relatedToFeature)) || this.sortedAllDisabledFeatures.binaryContains(entity.entityType)) {
          disabledEntities.push(entity.entityType);
        } else {
          this.sortedAllEntityTypes.push(entity.entityType);
        }
      }
      for (let simpleEntityCodeMetaData of module.entitiesMetaData?.simpleEntitiesMetaData ?? []) {
        this.simpleEntityTypesMetadata[simpleEntityCodeMetaData.entityType!] = simpleEntityCodeMetaData;
      }
    }
    if (settings.customGUIPartsReponse?.modules) {
      for (let module of settings.customGUIPartsReponse.modules) {
        if (!this.installedModuleIDs.contains(module))
          this.installedModuleIDs.push(module);
      }
    }
    this.sortedAllDisabledFeatures.push(...disabledEntities);
    this.sortedAllDisabledFeatures.sort();
    this.sortedAllEntityTypes.sort()
    this.installedModuleIDs.sort();
    let loginInfo = LoginUtils.loginInfo;
    let loginDimensions = loginInfo?.dimensions;
    let legId = loginDimensions?.legalEntity?.id;
    let sectorId = loginDimensions?.sector?.id;
    let branchId = loginDimensions?.branch?.id;
    let deptId = loginDimensions?.department?.id;
    let anaLysisId = loginDimensions?.analysisSet?.id;
    const params: NamaAxiosRequestConfig = {
      params: {
        request: JSON.stringify({
          userId: loginInfo?.userInfo?.id,
          barCodeOn: loginInfo?.barCodeOn,
        })
      }, noLoadingIndicator: true,
    };
    let result = await NamaCommon.namaFetch(NamaURLS.FetchAutoDashboardsAndReports, {});
    this.autoDashboardsAndReports = (result?.data || {}) as DTOAutoDashboardsAndReports;
    await this.getDashboardGroupItemsFromMenu();
    let data = await NamaCommon.namaFetch(NamaURLS.FetchDimsConfig, params)
    this.dimensionConfigurations = data.data;
    if (this.dimensionConfigurations == null)
      this.dimensionConfigurations = {};
    if (settings.customGUIPartsReponse?.useBackgroundImage) {
      this.bgImageUrl = `${GUIDevMode.APP_URL}image.download?fileID=${CommonConstants.BG_IMAGE}&&${URLRequestParameters.LEGAL_ENTITY}=${legId}&&${URLRequestParameters.SECTOR}=${sectorId}&&${URLRequestParameters.BRANCH}=${branchId}&&${URLRequestParameters.DEPARTMENT}=${deptId}&&${URLRequestParameters.ANALYSIS_SET}=${anaLysisId}`
    }
    await ClientCache.nextGUIDId();
  }

  static getBgImageUrl(): string {
    if (this.bgImageUrl)
      return this.bgImageUrl;
    return '';
  }

  static async getDashboardGroupItemsFromMenu() {
    let items = LoginUtils.loginInfo?.menu?.items;
    if (!items)
      return;
    let allDashGroups = items?.filter(menuItem =>
      MenuTargetType.DashboardsGroup === menuItem.target?.type)
      .map(menuItem => menuItem.target?.targetRecord) ?? [];
    if (this.autoDashboardsAndReports)
      allDashGroups.push(...this.autoDashboardsAndReports.autoDashboardGroups ?? []);
    if (!allDashGroups)
      return;
    let data = await NamaCommon.namaFetch(NamaURLS.FetchDashboardGroupItems, {
      data: allDashGroups
    });
    if (data && data.list)
      (data.list as EntityReferenceData[]).forEach(dashboard => this.addDashboardToGroup(dashboard));
  }

  static addDashboardToGroup(dashboard: EntityReferenceData) {
    if (!dashboard)
      return;
    let ownerGroup: string | undefined | null = (dashboard.extraData ?? {})[DomainBaseFieldIDs.DashBoard_details_dashBoardGroup];
    if (!ownerGroup)
      return;
    CollectionUtils.getOrAdd(ownerGroup, this.dashboardGroups).push(dashboard);
  }

  static getDashboardsOfGroup(group: EntityReferenceData | undefined): EntityReferenceData[] {
    if (!group)
      return [];
    if (!this.dashboardGroups)
      return [];
    return this.dashboardGroups[group.id!];
  }

  static fetchCriticalMsgs() {
    let customGUISettings = this.getCustomGUISettings();
    if (!customGUISettings)
      return null;
    if (!customGUISettings.criticalSystemMessages)
      return null;
    this.displayCriticalCount++;
    if (this.displayCriticalCount <= 3)
      return customGUISettings.criticalSystemMessages;
  }

  static getTemplates(): TemplateMetadata[] {
    return this.customGUISettings?.templates ?? [];
  }

  static getEntityTemplates(entityType: string): TemplateMetadata[] {
    return this.getTemplates().filter(object => ObjectChecker.areEqual(object.entityType, entityType));
  }

  static currentTimeMillis() {
    let date = new Date();
    return date.getTime();
  }

  static fetchTargetType(menuItem: namaTypes.DTOMenuItem) {
    if (!menuItem.target)
      return null;
    return menuItem.target.type;
  }

  static getAutoDashBoardGroups(): EntityReferenceData[] {
    if (!this.autoDashboardsAndReports)
      return [];
    let autoDashboardGroups: EntityReferenceData[] = this.autoDashboardsAndReports.autoDashboardGroups || [];
    let orderInMenu = DomainBaseFieldIDs.DashBoardGroup_orderInMenu;
    autoDashboardGroups.sort((o1, o2) =>
      NamaCommon.namaCompare((o1.extraData ?? {})[orderInMenu] ?? "", (o2.extraData ?? {})[orderInMenu]));
    return autoDashboardGroups;
  }

  static getRootReportGroups() {
    if (this.rootReportGroupsMergedWithAuto || !this.autoDashboardsAndReports)
      return this.rootReportGroups;
    let autoGroups = this.autoDashboardsAndReports.autoReportGroups || [];
    autoGroups.sort((o1, o2) =>
      NamaCommon.namaCompare((o1.extraData ?? {})[DomainBaseFieldIDs.ReportGroup_orderInMenu], (o2.extraData ?? {})[DomainBaseFieldIDs.ReportGroup_orderInMenu]));
    for (let g of autoGroups) {
      this.rootReportGroups?.splice(this.getGroupIndex(g), 0, g);
    }
    this.rootReportGroupsMergedWithAuto = true;
    return this.rootReportGroups;
  }

  static getGroupIndex(g) {
    let o = g.extraData[DomainBaseFieldIDs.ReportGroup_orderInMenu];
    if (!o)
      return 0;
    return Math.min(this.rootReportGroups?.length ?? 0, parseInt(o) - 1);
  }

  static isModuleLoaded(moduleId: string | undefined) {
    if (!moduleId)
      return true;
    return this.installedModuleIDs.binaryContains(moduleId)
  }

  static isModuleNotEmptyAndLoaded(moduleId: string | undefined) {
    if (!moduleId)
      return false;
    return this.installedModuleIDs.binaryContains(moduleId)
  }

  static getAllEntityTypes() {
    return this.sortedAllEntityTypes;
  }

  static getSortedDisbaledFeatures() {
    if (!this.sortedAllDisabledFeatures) {
      this.initializeRelatedFeatures();
    }
    return this.sortedAllDisabledFeatures;
  }

  static initializeRelatedFeatures() {
    this.sortedAllDisabledFeatures = [...LoginUtils.loginInfo?.userPreventedEntities ?? [], ...LoginUtils.loginInfo?.userPreventedFeatures ?? []];
    this.sortedAllDisabledFeatures.sort();
  }

  static getMenuLineBySrc(src: any): DTOMenuItem | null {
    if (!src)
      return null;
    let menu = LoginUtils.loginInfo?.menu;
    for (let item of menu?.items ?? []) {
      if (ObjectChecker.isAnyEqualToFirst(src, item.id, item.code))
        return item;
    }
    return null;
  }

  static get uiPreferencesCache(): Record<string, UIPreferences | null> {
    return this._uiPreferencesCache;
  }

  static get termsAndConfigScreens(): Record<string, namaTypes.EditScreenLayout | null> {
    return this._termsAndConfigScreens;
  }

  static getListLayoutIfFetched(entityType: string, listViewName: string | undefined): ListScreenLayout | undefined {
    let uiPreferences = this.uiPreferencesCache[entityType];
    if (uiPreferences && uiPreferences.listLayout && MetaData.isTheSameLayout(uiPreferences.listLayout.id, listViewName))
      return uiPreferences.listLayout;
    return;
  }

  static async isPrintingPossible(request: PrintReportRequest): Promise<boolean> {
    let key: string = this.printPossibleKey(request);
    let cachedResponse: boolean = this.isPrintPossibleMap[key];
    if (ObjectChecker.isNotNullOrUndefined(cachedResponse)) {
      return cachedResponse;
    } else {
      let params: AxiosRequestConfig = {
         data: NamaCommon.stringify(request)
      }
      let result: boolean = await NamaCommon.namaFetch(NamaURLS.IsPrintReportPossible, params);
      this.isPrintPossibleMap[key] = result;
      return result;
    }
  }

  static printPossibleKey(request: PrintReportRequest): string {
    return request.entityType + CommonConstants.SEPARATOR + request.tabId + CommonConstants.SEPARATOR + request.viewName + CommonConstants.SEPARATOR
      + request.form;
  }

  static shouldPrintUsingNamaServer() {
    return (this.customGUISettings && this.customGUISettings.printUsingNamaServer) || (LoginUtils.loginInfo && LoginUtils.loginInfo.directPrint);
  }

  static isEntityEnabled(entityType: string | null | undefined) {
    return !entityType || this.isSimpleEntity(entityType) || this.sortedAllEntityTypes?.includes(entityType);
  }

  static getReportsSubsidiaryTypes(): string[] {
    return this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.reportsSubsidiaryTypes ?? [];
  }

  static isSimpleEntity(entityType: string | null | undefined) {
    return this.simpleEntityTypesMetadata && this.simpleEntityTypesMetadata[entityType!] != null;
  }

  static getEntityImageInfo(entityType: string): namaTypes.EntityMainFileInfo | null {
    if (!this.getCustomGUISettings())
      return null;
    if (!this.getCustomGUISettings()!.tooltipsAndReportsAndRevise)
      return null;
    if (!this.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.entityMainFileInfo)
      return null;
    if (!this.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.entityMainFileInfo!.entitiesInfo)
      return null;
    return this.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.entityMainFileInfo!.entitiesInfo![entityType];
  }

  static doesFieldHaveAllowedValues(entityType: string, fieldId: string | undefined) {
    if (!ClientCache.getCustomGUISettings())
      return false;
    if (!ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise)
      return false;
    if (!ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.fieldsAllowedValues)
      return false;
    if (!ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.fieldsAllowedValues!.entityTypeFieldValuesMap)
      ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.fieldsAllowedValues!.entityTypeFieldValuesMap = {};
    let metadata: namaTypes.FieldAllowedValueInfo = ClientCache.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.fieldsAllowedValues!
      .entityTypeFieldValuesMap![entityType + '.' + fieldId];
    return metadata != null && ObjectChecker.isNotEmptyOrNull(metadata.allowedValues);
  }

  static getAllowedValuesFor(entityType: string, fieldId: string): string[] {
    let fieldsAllowedValues: namaTypes.FieldsAllowedValuesMetadata | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.fieldsAllowedValues;
    let metadata: namaTypes.FieldAllowedValueInfo | null = fieldsAllowedValues?.entityTypeFieldValuesMap ? fieldsAllowedValues.entityTypeFieldValuesMap[entityType + "." + fieldId] : null;
    if (metadata == null)
      return [];
    return metadata.allowedValues ?? [];
  }

  static getDoNotUseTodayAndYesterdayForDates() {
    return this.getCustomGUISettings() == null || this.getCustomGUISettings()!.tooltipsAndReportsAndRevise == null ?
      false : this.getCustomGUISettings()!.tooltipsAndReportsAndRevise!.doNotUseTodayAndYesterdayForDates;
  }

  static getDefaultDisplayFractionalDecimalPlaces() {
    return this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.defaultDisplayDecimalPlaces
  }

  static getFieldDisplayMask(entityType: string | undefined, fieldId: string | undefined) {
    let customizedFieldsInfo = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo;
    if (!customizedFieldsInfo || !fieldId)
      return undefined;
    let mask = this.recursivelyFindMask(entityType, fieldId, customizedFieldsInfo.entityFieldMasks);
    if (mask)
      return mask;
    return this.recursivelyFindMask(null, fieldId, customizedFieldsInfo.genericFieldMasks);
  }

  static recursivelyFindMask(entityType: string | null | undefined, fieldId: string, map: Record<string, string> | undefined) {
    if (!map)
      return null;
    let searchId: string = ObjectChecker.isEmptyOrNull(entityType) ? fieldId : entityType + "." + fieldId;
    let mask: string = map[searchId];
    if (mask != null)
      return mask;
    if (ObjectChecker.areEqual(fieldId, "*"))
      return null;
    if (fieldId.endsWith(".*"))
      fieldId = fieldId.substring(0, fieldId.length - 2);
    if (fieldId.includes("."))
      fieldId = StringUtils.substringBeforeLast(fieldId, ".") + ".*";
    else
      fieldId = "*";
    return this.recursivelyFindMask(entityType, fieldId, map);
  }

  static isHijriField(fieldId: string, entityType: string) {
    if (LoginUtils.loginInfo != null && LoginUtils.loginInfo.hijriDate)
      return true;
    if (ClientCache.customGUISettings?.hijriFields?.binaryContains(fieldId)
      || ClientCache.customGUISettings?.hijriFields?.binaryContains(entityType + "." + fieldId))
      return true;
    return false;
  }

  static findEntityMaxReviseCount(entityType: string): ReviseSettings {
    let reviseLevel: number | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.defaultReviseLevel;
    let useReviseDocument: boolean | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.useReviseDocumentForAllEntities;
    let reviseSettings: ReviseSettings = {
      reviseLevel: reviseLevel ? reviseLevel : 1,
      useReviseDocument: useReviseDocument ? useReviseDocument : false
    };
    if (this.customGUISettings?.tooltipsAndReportsAndRevise?.entitiesReviseLevelsMap && this.customGUISettings?.tooltipsAndReportsAndRevise?.entitiesReviseLevelsMap[entityType]) {
      reviseSettings = this.customGUISettings.tooltipsAndReportsAndRevise.entitiesReviseLevelsMap[entityType];
    }
    return reviseSettings;
  }

  static isDisabledField(entityType: string, fieldId: string | undefined) {
    if (NamaCommon.binarySearch(this.getEntityDisabledFields()!, entityType + "." + fieldId, NamaCommon.defaultCompare) >= 0)
      return true;
    return NamaCommon.binarySearch(this.getGenericDisabledFields()!, fieldId, NamaCommon.defaultCompare) >= 0;
  }

  static getGenericDisabledFields() {
    return this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.genericDisabledFields;
  }

  static getEntityDisabledFields() {
    return this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.entityDisabledFields;
  }

  static shouldShowSearchInForTopPanel() {
    if (this.customGUISettings == null)
      return false;
    return this.customGUISettings.tooltipsAndReportsAndRevise && this.customGUISettings.tooltipsAndReportsAndRevise.showSearchInForTopPanel;
  }

  static mustSelectEntityInSearchInBeforeSearchOnServer() {
    if (this.customGUISettings == null)
      return false;
    return this.shouldShowSearchInForTopPanel() && this.customGUISettings.tooltipsAndReportsAndRevise
      && this.customGUISettings.tooltipsAndReportsAndRevise.mustSelectEntityInSearchInBeforeSearchOnServer;
  }

  static isFieldValuesRestricted(entityType: string, fieldId: string | undefined) {
    let fieldsAllowedValues: namaTypes.FieldsAllowedValuesMetadata | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.fieldsAllowedValues;
    let metadata: namaTypes.FieldAllowedValueInfo | null = fieldsAllowedValues?.entityTypeFieldValuesMap ? fieldsAllowedValues.entityTypeFieldValuesMap[entityType + "." + fieldId] : null;
    return metadata && metadata.restrict;
  }

  static getSimpleEntityTypes(): string[] {
    return Object.keys(this.simpleEntityTypesMetadata);
  }

  static getSimpleAndAllEntityTypes(): string[] {
    if (this._simpleAndAllEntityTypes)
      return this._simpleAndAllEntityTypes;
    return this._simpleAndAllEntityTypes = this.getSimpleEntityTypes().concat(this.getAllEntityTypes());
  }

  static getDocumentFileEntityTypes(): string[] {
    let allEntityTypes: string[] = this.getAllEntityTypes();
    let documentFiles: string[] = [];
    for (let entity of allEntityTypes) {
      if (EntityTypesUtils.isDocument(entity))
        documentFiles.push(entity);
    }
    return documentFiles;
  }

  static getMasterFileEntityTypes(): string[] {
    let allEntityTypes: string[] = this.getAllEntityTypes();
    let masterFiles: string[] = [];
    for (let entity of allEntityTypes) {
      if (EntityTypesUtils.isMaster(entity))
        masterFiles.push(entity);
    }
    return masterFiles;
  }

  static isRichTextField(entityType: string, fieldId: string | undefined) {
    let customizedFieldsInfo: namaTypes.CustomizedFieldsInfo | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.customizedFieldsInfo;
    return NamaCommon.binarySearch(customizedFieldsInfo?.entityRichTextFields ?? [], entityType + "." + fieldId, NamaCommon.defaultCompare) >= 0
      || NamaCommon.binarySearch(customizedFieldsInfo?.genericRichTextFields ?? [], fieldId, NamaCommon.defaultCompare) >= 0;
  }

  static isBarcodeTextField(fieldId: string | undefined) {
    return fieldId === "barcode";
  }

  static isSoundEffectField(fieldId: string | undefined) {
    let id = fieldId?.split('.')?.pop();
    return id === 'notificationsSound' || id === 'approvalsSound' || id === 'messagesSound' || id === 'taskEndedSound';
  }

  static isTextToLink(accessor, fieldId: string) {
    if (!accessor)
      return false;
    return this.isTextToNaMaField(accessor.entityType, fieldId,
      this.customGUISettings?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.textToLinks);
  }

  static isTextToEmail(accessor: any, fieldId: string | undefined) {
    if (!accessor || !fieldId)
      return false;
    return this.isTextToNaMaField(accessor.entityType, fieldId,
      this.customGUISettings?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.textToEmails);
  }

  static isTextToPhoneNumber(accessor: any, fieldId: string | undefined) {
    if (!accessor || !fieldId)
      return false;
    return this.isTextToNaMaField(accessor.entityType, fieldId,
      this.customGUISettings?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.textToPhones);
  }

  static isTextToNaMaField(entityType, fieldId: string, textTo?: string[]) {
    return NamaCommon.binarySearch(textTo ?? [], entityType + "." + fieldId, NamaCommon.defaultCompare) >= 0
      || NamaCommon.binarySearch(textTo ?? [], fieldId, NamaCommon.defaultCompare) >= 0;
  }

  static calcFieldValueTriggeredTemplate(entityType: string, fieldId: string, value: any) {
    if (!value || !this.customGUISettings?.tooltipsAndReportsAndRevise?.templeteMap)
      return null;
    let templateTriggerList: namaTypes.TemplateTriggerMetaDataList = this.customGUISettings.tooltipsAndReportsAndRevise.templeteMap[entityType + '.' + fieldId];
    if (!templateTriggerList || !templateTriggerList.templates || !templateTriggerList.templates.length)
      return null;
    for (let template of templateTriggerList.templates) {
      if (this.isTemplateMatchValue(value, template))
        return template.templateId;
    }
    return null;
  }

  static isTemplateMatchValue(value: any, templateTrigger: namaTypes.TemplateTriggerMetaData) {
    if (value == templateTrigger.dateValue || value == templateTrigger.numericValue || value == templateTrigger.textValue || value == templateTrigger)
      return true;
    if (typeof value === 'object' && value.id && templateTrigger.refValue)
      return value.id == templateTrigger.refValue.id;
    return false;
  }

  static getRateScale(): number {
    if (this.customGUISettings?.rateFractionalDecimalPlaces)
      return this.customGUISettings.rateFractionalDecimalPlaces;
    else
      return CommonConstants.RATE_SCALE;
  }

  static getPercentageScale(): number {
    if (this.customGUISettings?.percentageFractionalDecimalPlaces)
      return this.customGUISettings.percentageFractionalDecimalPlaces;
    else
      return CommonConstants.PERCENTAGE_SACLE;

  }

  static getFieldStyle(entityType: string, fieldId: string): FieldStyle | null | undefined {
    if (!this.customGUISettings?.tooltipsAndReportsAndRevise?.customizedFieldsInfo)
      return null;
    let styleMap = this.customGUISettings.tooltipsAndReportsAndRevise.customizedFieldsInfo.entityFieldStyleMap;
    if (!styleMap)
      return null;
    let style = styleMap[entityType + "." + fieldId];
    if (style)
      return style;
    styleMap = this.customGUISettings.tooltipsAndReportsAndRevise.customizedFieldsInfo.genericFieldStyleMap;
    if (!styleMap)
      return null;
    style = styleMap[entityType + "." + fieldId];
    if (!style)
      style = styleMap[fieldId];
    return style;
  }

  static isNotColorField(entityType: string, fieldId: string | undefined): boolean {
    let customizedFieldsInfo: namaTypes.CustomizedFieldsInfo | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.customizedFieldsInfo;
    return NamaCommon.binarySearch(customizedFieldsInfo?.notColorFields ?? [], entityType + "." + fieldId, NamaCommon.defaultCompare) >= 0
      || NamaCommon.binarySearch(customizedFieldsInfo?.notColorFields ?? [], fieldId, NamaCommon.defaultCompare) >= 0;
  }

  static calcExtraReferenceSearchSettings(entityType: string) {
    let isMaster = EntityTypesUtils.isMaster(entityType);
    let info = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.extraReferenceSearchSetting;
    if (!entityType || !info)
      return [];
    let settings: Record<string, any> = info[entityType];
    let allScreens: Record<string, any> = info["AllScreens"];
    let documentOrMaster: Record<string, any> = info[isMaster ? "MasterFiles" : "Documents"];
    if (ObjectChecker.areAllEmptyOrNull(settings, allScreens, documentOrMaster))
      return [];
    let arr: any[] = [];
    if (settings)
      arr.push(...settings['list'])
    if (documentOrMaster)
      arr.push(...documentOrMaster['list'])
    if (allScreens)
      arr.push(...allScreens['list'])
    return arr;
  }

  static getAllGUIActions(): string[] {
    const loginInfoStore = useLoginInfoStore();
    if (!LoginUtils.loginInfo || !loginInfoStore.isLoggedIn)
      return [];
    return LoginUtils.loginInfo.definedGUIActions ?? [];
  }

  static isAttachmentSignature(entityType: string, fieldId: string, isApprovalContext: boolean = false): boolean {
    if (fieldId === "attachment1" && isApprovalContext && this.getCustomGUISettings()?.useSignatureInApprovalAttachment)
      return true;
    let customizedFieldsInfo: CustomizedFieldsInfo | undefined = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo;
    if (!customizedFieldsInfo || !customizedFieldsInfo.entitySignatureFields)
      return false;
    if (customizedFieldsInfo.entitySignatureFields[entityType + "." + fieldId] || customizedFieldsInfo.entitySignatureFields[fieldId])
      return true;
    return false;
  }

  static isAttachmentUsingScannerByDefault(entityType: string, id: string): boolean {
    let info: UseScannerInFieldInfo | undefined = this.fetchAUseScannerInfo(entityType, id);
    if (!info)
      return false;
    return !info.useNormalUploadByDefault;
  }

  static fetchAUseScannerInfo(entityType: string, id: string): UseScannerInFieldInfo | undefined {
    let customizedFieldsInfo: CustomizedFieldsInfo | undefined = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo;
    if (!customizedFieldsInfo || !customizedFieldsInfo.useScannerInFields)
      return;
    let scanInfo: UseScannerInFieldInfo | undefined = customizedFieldsInfo.useScannerInFields[entityType + "." + id];
    if (!scanInfo)
      scanInfo = customizedFieldsInfo.useScannerInFields[entityType + ".*"];
    if (!scanInfo)
      scanInfo = customizedFieldsInfo.useScannerInFields[id];
    if (!scanInfo)
      scanInfo = customizedFieldsInfo.useScannerInFields["*"];
    return scanInfo;
  }

  static fetchSignatureLineApplied(entityType: string, id: string): ESignatureInfo {
    let customizedFieldsInfo: CustomizedFieldsInfo | undefined = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo;
    let singInfo: ESignatureInfo = {};
    if (!customizedFieldsInfo)
      return singInfo;
    if (customizedFieldsInfo.entitySignatureFields)
      singInfo = customizedFieldsInfo.entitySignatureFields[entityType + "." + id];
    if (!singInfo && customizedFieldsInfo.genericSignatureFields)
      singInfo = customizedFieldsInfo.genericSignatureFields[id];
    return singInfo || {};
  }

  static isFifoCost(): boolean {
    return ObjectChecker.areEqual(this.customGUISettings?.tooltipsAndReportsAndRevise?.costOutType, "FIFO");
  }

  static getEntityCustomReportsFromMetaData(entityType: string): CustomRepInfo[] {
    let customReportsMetadata: CustomReportsMetadata | undefined = this.customGUISettings?.tooltipsAndReportsAndRevise?.customReportsMetadata
    let list: CustomRepInfo[] = [];
    let indexList: Record<any, any> = {};
    if (customReportsMetadata && customReportsMetadata.indices)
      indexList = customReportsMetadata.indices[entityType];
    if (customReportsMetadata && customReportsMetadata.customReports && indexList) {
      for (let index of indexList['indices']) {
        list.push(customReportsMetadata.customReports[index]);
      }
    }
    return list;
  }

  static getEntityCustomReports(ownerId: string | undefined): CustomRepInfo[] {
    let docOrMaster: string = CriteriaValidatorApplicability.MasterFiles;
    if (ownerId && EntityTypesUtils.isDocument(ownerId))
      docOrMaster = CriteriaValidatorApplicability.Documents;
    let customRepInfo: CustomRepInfo[] = []
    if (ownerId)
      customRepInfo.push(...this.getEntityCustomReportsFromMetaData(ownerId));
    customRepInfo.push(...this.getEntityCustomReportsFromMetaData(CriteriaValidatorApplicability.AllScreens));
    customRepInfo.push(...this.getEntityCustomReportsFromMetaData(docOrMaster));
    return customRepInfo;
  }

  static getTooltipMetadata(): TooltipMetadata | undefined {
    return this.customGUISettings?.tooltipsAndReportsAndRevise?.tooltipMetadata;
  }

  static getTooltipPosition(): string {
    return this.customGUISettings?.tooltipsAndReportsAndRevise?.tooltipPosition ?? "Bottom";
  }

  static isPinTooltip(): boolean {
    return this.customGUISettings?.tooltipsAndReportsAndRevise?.pinToolTip ?? false;
  }

  static isGoogleDocsPreviewEnabled(): boolean {
    return !(this.customGUISettings?.tooltipsAndReportsAndRevise?.dontUseGoogleDocsForOfficeFilesPreview ?? false);
  }

  static syncNextGUIDId(): string {
    const firstId = this.generatedIDs.shift();
    if (!firstId?.length)
      throw new Error("No IDS");
    else
      return firstId;
  }

  static async asyncNextGUIDId() {
    await this.nextGUIDId();
    return this.generatedIDs.shift()!;
  }

  static async nextGUIDId() {
    if (this.fetchingIdsFromServer)
      return;
    if (this.generatedIDs.length < 25)
      try {
        this.fetchingIdsFromServer = true;
        this.generatedIDs.push(...await NamaCommon.namaFetch(NamaURLS.NEW_IDS, {noLoadingIndicator: true}));
      } finally {
        this.fetchingIdsFromServer = false;
      }
  }
  static calcAllowedValuesForEntityType(metadata: FieldMetaData): string[] {
    let allowedValues: string[] = metadata.allowedValues ?? [];
    if (!allowedValues.length) {
      allowedValues = ClientCache.getAllEntityTypes();
    } else if (allowedValues.length === 1) {
      const firstAllowedValue = allowedValues[0];
      if (firstAllowedValue === CommonConstants.GENERIC_REF_ALL || !firstAllowedValue)
        allowedValues = ClientCache.getAllEntityTypes();
      else if (firstAllowedValue === CommonConstants.GENERIC_REF_SIMPLE_AND_ALL)
        allowedValues = ClientCache.getSimpleAndAllEntityTypes();
      else if (firstAllowedValue === CommonConstants.GENERIC_REF_ALL_DOCS)
        allowedValues = ClientCache.getDocumentFileEntityTypes();
      else if (firstAllowedValue === CommonConstants.GENERIC_REF_ALL_MASTERS)
        allowedValues = ClientCache.getMasterFileEntityTypes();
    }
    if (allowedValues.length && ObjectChecker.areEqual(metadata.fieldType, FieldType.EntityType))
      allowedValues = allowedValues.filter(v => ClientCache.isEntityEnabled(v));
    return allowedValues;
  }
  static fetchingIdsFromServer = false;

  static fetchTooltipFieldIds(entityType?: string): string[] {
    let tooltipMetadata = ClientCache.getTooltipMetadata();
    if (!tooltipMetadata)
      return [];
    let fieldsWithTooltips: string[] = [];
    if (entityType) {
      for (let key in tooltipMetadata.fieldsTooltips) {
        if (key.startsWith(entityType + '#'))
          fieldsWithTooltips.push(key.substring(key.indexOf('#') + 1, key.length));
      }
    }
    let screenFields = TopPanelSearchUtils.getFieldSearchers().map(searcher => searcher("")).flatMap(a => a).map(f => f.id).filter(id => id);
    for (let fieldId in tooltipMetadata.defaultTooltips) {
      if (fieldsWithTooltips.contains(fieldId))
        continue;
      if (screenFields.contains(fieldId))
        fieldsWithTooltips.push(fieldId);
    }
    return fieldsWithTooltips;
  }

  static isOpenCreate(entityType?: string, fieldId?: string) {
    if (!fieldId || !entityType)
      return false;
    let openCreateFields = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.entityOpenCreateFields;
    if (!openCreateFields)
      return false;
    return openCreateFields.hasOwnProperty((entityType ? (entityType + ".") : "") + fieldId) || openCreateFields.hasOwnProperty(fieldId)
      || openCreateFields.hasOwnProperty(entityType ? (entityType + ".*") : "") || openCreateFields.hasOwnProperty("*");
  }

  static fetchFieldToSetValueInForOpenCreate(entityType?: string, fieldId?: string) {
    if (!entityType || !fieldId)
      return null;
    let openCreateFields = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.customizedFieldsInfo?.entityOpenCreateFields;
    if (!openCreateFields)
      return null;
    let field = openCreateFields[(entityType ? (entityType + ".") : "") + fieldId];
    if (field)
      return field;
    field = openCreateFields[fieldId];
    if (field)
      return field;
    field = openCreateFields[entityType ? (entityType + ".*") : ""];
    if (field)
      return field;
    field = openCreateFields["*"];
    return field;
  }

  static calcReferenceSearchingOperator(entityType: string | null | undefined, fieldId: string, isMaster: boolean, operatorsLineMap: Record<string, any> | null | undefined, operatorFunc: (line: any) => string, ignoreWordOrder: boolean = false): string {
    const defaultOperator = ignoreWordOrder ? Operator.ContainsWithAnyOrder.toString() : Operator.Contains.toString();
    if (ObjectChecker.isEmptyOrNull(entityType) || ObjectChecker.isEmptyOrNull(operatorsLineMap))
      return defaultOperator;
    const key1 = `${entityType}${CommonConstants.SEPARATOR}${fieldId}`;
    const key2 = `${isMaster ? "MasterFiles" : "Documents"}${CommonConstants.SEPARATOR}${fieldId}`;
    const key3 = `AllScreens${CommonConstants.SEPARATOR}${fieldId}`;
    const operatorsLines = ObjectChecker.getFirstNotNullObj(operatorsLineMap[key1], operatorsLineMap[key2], operatorsLineMap[key3]);
    if (ObjectChecker.isEmptyOrNull(operatorsLines) || ObjectChecker.isEmptyOrNull(operatorsLines["list"]))
      return defaultOperator;
    return operatorFunc(operatorsLines["list"][0]);
  }

  static calcReferenceCodeOperator(entityType: string, fieldId: string): string {
    return this.calcReferenceSearchOperators(entityType, fieldId, (line) => line.codeOperator);
  }

  static calcReferenceName1Operator(entityType: string, fieldId: string): string {
    return this.calcReferenceSearchOperators(entityType, fieldId, (line) => line.name1Operator);
  }

  static calcReferenceName2Operator(entityType: string, fieldId: string): string {
    return this.calcReferenceSearchOperators(entityType, fieldId, (line) => line.name2Operator);
  }

  static calcReferenceSearchOperators(entityType: string, fieldId: string, operatorFunc: (line: any) => string): string {
    const settings = this.getCustomGUISettings()?.tooltipsAndReportsAndRevise;
    return this.calcReferenceSearchingOperator(entityType, fieldId, EntityTypesUtils.isMaster(entityType), settings?.referenceSearchingOperators, operatorFunc, settings?.ignoreWordOrderInSearch === true);
  }
}
